import { useMemo } from 'react'
import dayjs from 'date'
import { constants, formatPrice, formatSize, object } from 'helpers'
import { useFt } from 'hooks'


type Data = Partial<Omit<PaymentModule.InquireItems, '__typename'>>

// returns order items from inquire, cart, payment session
const useOrderItems = (data: Data): OrderItems.Items => {
  const isCartEcommercePromosEnabled = useFt(constants.features.cartEcommercePromos)
  const isHolidaysGiftSubscriptionEnabled = useFt(constants.features.holiday22GiftSubscriptionOffer)

  return useMemo(() => {
    if (!data) {
      return {}
    }

    const { giftCard, giftSubscription, products, queueItems, subscription, subscriptionCase, subscriptionPromos, ecommercePromos } = data

    const items: OrderItems.Items = {}

    if (subscription) {
      const { price, plan, coupon } = subscription

      const couponFreeProduct = coupon?.freeProduct
      const freeProduct = couponFreeProduct ? {
        id: couponFreeProduct.id,
        name: couponFreeProduct.name,
        brand: couponFreeProduct.brandInfo.name,
        image: couponFreeProduct.image,
        rebrandImage: couponFreeProduct.rebrandImage,
        size: formatSize(couponFreeProduct.volume, couponFreeProduct.unit),
      } : null

      const subscriptionPromosItems = subscriptionPromos?.map(({ title, amount, promo }) => ({
        title,
        amount,
        promo: {
          ...promo,
          size: formatSize(promo.volume, promo.unit),
        },
      }))

      items.subscription = {
        deliveryDate: dayjs().add(5, 'days'), // TODO receive it from the backend - added on 2021-09-08 by mikediam
        price: formatPrice(price),
        plan: plan as any,
        subscriptionPromos: subscriptionPromosItems,
        subscriptionCase,
        freeProduct,
      }
    }

    if (queueItems) {
      items.queueItems = queueItems.map(({ id, price, product }) => ({
        ...product,
        id,
        quantity: 1,
        brand: product.brandInfo.name,
        size: `${product.volume} ${product.unit}`,
        price: formatPrice(price),
      }))
    }

    if (giftSubscription) {
      const { plan, price, label, recipient, offer } = giftSubscription

      items.giftSubscription = {
        months: plan.billingPeriod,
        price: formatPrice(price),
        label: label,
        recipient: object.removeKeys(recipient, '__typename'),
      }

      if (isHolidaysGiftSubscriptionEnabled && offer) {
        items.giftSubscriptionOffer = offer
      }
    }

    if (giftCard) {
      const { product, price, type, recipient, sender } = giftCard

      items.giftCard = {
        productId: product.id,
        price: formatPrice(price, { removeZeroCents: true }),
        type: type,
        recipient: object.removeKeys(recipient, '__typename'),
        sender: object.removeKeys(sender, '__typename'),
      }
    }

    const offerItemsByParentId = {}
    const promoProducts = []

    if (isCartEcommercePromosEnabled && ecommercePromos) {
      ecommercePromos.forEach(({ id, price, quantity, product, offer, parentPurchaseTradingItemId }) => {
        const item: OrderItems.Product = {
          ...product,
          id,
          productId: product.id,
          brand: product.brandInfo.name,
          size: formatSize(product.volume, product.unit),
          quantity,
          price: price !== 0 ? formatPrice(price) : null,
          offer,
          offerProducts: [],
        }

        if (parentPurchaseTradingItemId) {
          const offerItems = offerItemsByParentId[parentPurchaseTradingItemId] || []

          offerItems.push(item)

          offerItemsByParentId[parentPurchaseTradingItemId] = offerItems
        }
        else {
          promoProducts.push(item)
        }
      })
    }

    if (products) {
      items.products = products.reduce((result, { id, price, quantity, product, offer, parentPurchaseTradingItemId }) => {
        // we use reference to this object
        offerItemsByParentId[id] = offerItemsByParentId[id] || []

        const item: OrderItems.Product = {
          ...product,
          id,
          productId: product.id,
          brand: product.brandInfo.name,
          size: formatSize(product.volume, product.unit),
          quantity,
          price: price !== 0 ? formatPrice(price) : null,
          offer,
          offerProducts: offerItemsByParentId[id],
        }

        // we merge all offers except TOSS IN
        if (offer === 'TOSSIN' || offer === 'TOSSIN_EMAIL') {
          result.push(item)
          return result
        }

        if (offer && parentPurchaseTradingItemId) {
          // if FT is enabled we get offer items from ecommercePromos
          if (!isCartEcommercePromosEnabled) {
            offerItemsByParentId[parentPurchaseTradingItemId] = (offerItemsByParentId[parentPurchaseTradingItemId] || []).push(item)
          }
        }
        else {
          result.push(item)
        }

        return result
      }, [])
    }

    if (items.products) {
      items.products.push(...promoProducts)
    }
    else {
      items.products = promoProducts
    }

    return items
  }, [ data, isCartEcommercePromosEnabled, isHolidaysGiftSubscriptionEnabled ])
}


export default useOrderItems
